import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, LogOut, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTenant } from "../context/TenantContext";

/**
 * Red persistent strip at the top of the shell shown ONLY when a platform
 * admin holds an active impersonation token. Clicking Exit restores the
 * original platform-admin token (stashed in localStorage) and reloads to
 * the /platform console.
 */
export default function ImpersonationBanner() {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (!user?.is_impersonating) return null;

  const exit = () => {
    setBusy(true);
    try {
      const original = localStorage.getItem("wc_token_platform");
      if (original) {
        localStorage.setItem("wc_token", original);
        localStorage.removeItem("wc_token_platform");
        toast.success("Exited impersonation");
      } else {
        // Fallback: just drop the token so they go back to the login page
        localStorage.removeItem("wc_token");
      }
      // Hard reload to re-hydrate every context with the restored token
      window.location.href = "/platform/commands";
    } catch (err) {
      toast.error("Couldn't exit impersonation");
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="flex items-center justify-between gap-4 px-4 py-2 mb-4 rounded-2xl text-xs border bg-sky-50 border-sky-300 text-sky-900 dark:bg-sky-500/15 dark:border-sky-400/40 dark:text-sky-100"
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="w-6 h-6 rounded-full inline-flex items-center justify-center shrink-0 bg-sky-600 text-white">
          <Crown className="w-3.5 h-3.5" />
        </span>
        <span className="truncate">
          You're viewing {tenant?.name || "this shop"} as the platform admin. <b data-testid="impersonation-readonly-label">Read-only view</b> — changes are disabled.
        </span>
      </div>
      <button
        onClick={exit}
        disabled={busy}
        data-testid="exit-impersonation-btn"
        className="flex items-center gap-1 bg-sky-600 text-white font-semibold text-[11px] uppercase tracking-widest px-3 py-1 rounded-full hover:bg-sky-500 transition shrink-0 disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <LogOut className="w-3 h-3" />}
        Exit
      </button>
    </div>
  );
}
